// API backend admin sendiri (menggantikan CMS Directus sepenuhnya)
export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export async function fetchStudyPrograms() {
  const res = await fetch(`${API_URL}/api/public/program-studi`);
  if (!res.ok) throw new Error('Gagal mengambil program studi');
  return res.json();
}

export async function fetchAcademicCalendar() {
  const res = await fetch(`${API_URL}/api/public/kalender-akademik`);
  if (!res.ok) throw new Error('Gagal mengambil kalender akademik');
  return res.json();
}

export async function fetchStudyProgram(slug) {
  const res = await fetch(`${API_URL}/api/public/program-studi/${encodeURIComponent(slug)}`);
  if (!res.ok) throw new Error('Program studi tidak ditemukan');
  return res.json();
}

// Sarana & Prasarana — dari backend admin (hanya item published)
export const fetchSaranaPrasarana = async () => {
  const res = await fetch(`${API_URL}/api/public/sarana-prasarana`);
  if (!res.ok) throw new Error('Gagal memuat sarana & prasarana');
  return res.json();
};

export const fetchSaranaPrasaranaBySlug = async (slug) => {
  const res = await fetch(`${API_URL}/api/public/sarana-prasarana/${encodeURIComponent(slug)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Gagal memuat detail sarana & prasarana');
  return res.json();
};

export const fetchDosen = async () => {
  try {
    const res = await fetch(`${API_URL}/api/public/staff/dosen`);
    if (!res.ok) throw new Error('Gagal memuat dosen');
    return await res.json();
  } catch (err) {
    console.error('Error fetching dosen:', err);
    return [];
  }
};

export const fetchDosenByIdentifier = async (identifier) => {
  const value = String(identifier || '').trim();
  if (!value) return null;
  try {
    const res = await fetch(`${API_URL}/api/public/staff/dosen/${encodeURIComponent(value)}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error('Gagal mengambil profil dosen');
    return await res.json();
  } catch (err) {
    console.error('Error fetching dosen by identifier:', err);
    return null;
  }
};

export const fetchTendik = async () => {
  try {
    const res = await fetch(`${API_URL}/api/public/staff/tendik`);
    if (!res.ok) throw new Error('Gagal memuat tendik');
    return await res.json();
  } catch (err) {
    console.error('Error fetching tendik:', err);
    return [];
  }
};

export const fetchTendikByIdentifier = async (identifier) => {
  const value = String(identifier || '').trim();
  if (!value) return null;
  try {
    const res = await fetch(`${API_URL}/api/public/staff/tendik/${encodeURIComponent(value)}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error('Gagal mengambil profil tendik');
    return await res.json();
  } catch (err) {
    console.error('Error fetching tendik by identifier:', err);
    return null;
  }
};

// Profil anggota struktur unit dapat berasal dari dosen maupun tendik. Jika
// tipe belum tersimpan pada data lama, tetap cari keduanya agar tautan dan
// foto profil tetap berfungsi.
export const fetchUnitMemberProfile = async (identifier, profileType) => {
  const loaders = profileType === 'dosen'
    ? [['dosen', fetchDosenByIdentifier], ['tendik', fetchTendikByIdentifier]]
    : [['tendik', fetchTendikByIdentifier], ['dosen', fetchDosenByIdentifier]];
  for (const [kind, loader] of loaders) {
    const profile = await loader(identifier);
    if (profile) return { profile, kind };
  }
  return null;
};

/// Susunan struktur (jabatan + orang) kini diturunkan dari bagan Struktur
/// Organisasi. Bentuk `{ jabatan, nama, foto, slug }` dipertahankan untuk
/// pemakainya (ProgramStudiDetail).
export const fetchStrukturOrganisasi = async () => {
  try {
    const res = await fetch(`${API_URL}/api/public/struktur-organisasi`);
    if (!res.ok) throw new Error('Gagal memuat struktur organisasi');
    const data = await res.json();
    return (data.members || []).map((m) => ({
      jabatan: String(m.position || '').replace(/\s+/g, ' ').trim(),
      nama: m.name,
      foto: m.photoUrl || null,
      slug: m.profileSlug || null,
    }));
  } catch (err) {
    console.error('Error fetching struktur organisasi:', err);
    return [];
  }
};

export const fetchBerita = async (limit = 10) => {
  try {
    const res = await fetch(`${API_URL}/api/public/berita?limit=${limit}`);
    if (!res.ok) throw new Error('Gagal memuat berita');
    return await res.json();
  } catch (err) {
    console.error('Error fetching berita:', err);
    return [];
  }
};

export const fetchAgenda = async (limit = 10) => {
  try {
    const query = Number(limit) > 0 ? `?limit=${limit}` : '';
    const res = await fetch(`${API_URL}/api/public/agenda${query}`);
    if (!res.ok) throw new Error('Gagal memuat agenda');
    return await res.json();
  } catch (err) {
    console.error('Error fetching agenda:', err);
    return [];
  }
};

export const fetchSlider = async () => {
  try {
    const res = await fetch(`${API_URL}/api/public/slider`);
    if (!res.ok) throw new Error('Gagal memuat slider');
    return await res.json();
  } catch (err) {
    console.error('Error fetching slider:', err);
    return [];
  }
};

export const fetchTestimoni = async () => {
  try {
    const res = await fetch(`${API_URL}/api/public/testimoni`);
    if (!res.ok) throw new Error('Gagal memuat testimoni');
    return await res.json();
  } catch (err) {
    console.error('Error fetching testimoni:', err);
    return [];
  }
};

export const fetchGaleriBeranda = async () => {
  try {
    const res = await fetch(`${API_URL}/api/public/galeri/beranda`);
    if (!res.ok) throw new Error('Gagal memuat galeri beranda');
    return await res.json();
  } catch (err) {
    console.error('Error fetching galeri beranda:', err);
    return [];
  }
};

/// Struktur album relasional jadi satu-satunya jalur; stub agar pemanggil lama
/// (fallback flat di Galeri.jsx) tidak error.
export const fetchGaleri = async () => [];

// Album galeri (galeri_album + foto). Bentuk item dipertahankan: cover & foto
// berupa URL penuh domain media.
export const fetchGaleriAlbum = async () => {
  try {
    const res = await fetch(`${API_URL}/api/public/galeri`);
    if (!res.ok) throw new Error('Gagal memuat galeri');
    return await res.json();
  } catch (err) {
    console.error('Error fetching galeri album:', err);
    return [];
  }
};

export const fetchUnduhan = async () => {
  try {
    const res = await fetch(`${API_URL}/api/public/unduhan`);
    if (!res.ok) throw new Error('Gagal memuat unduhan');
    return await res.json();
  } catch (err) {
    console.error('Error fetching unduhan:', err);
    return [];
  }
};

const fetchUnduhanOne = async (idOrSlug) => {
  try {
    const res = await fetch(`${API_URL}/api/public/unduhan/${encodeURIComponent(idOrSlug)}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error('Gagal memuat berkas');
    return await res.json();
  } catch (err) {
    console.error('Error fetching unduhan:', err);
    return null;
  }
};

export const fetchUnduhanById = fetchUnduhanOne;

export const fetchUnduhanBySlug = async (slug) => {
  const data = await fetchUnduhanOne(slug);
  return data && data.status !== 'draft' ? data : null;
};

// Pengumuman terbitan unit kerja dari backend admin. Bentuk itemnya sengaja
// menyerupai item berita Directus supaya bisa digabung pada daftar /berita.
export const fetchPengumumanUnit = async (unit) => {
  const query = unit ? `?unit=${encodeURIComponent(unit)}` : '';
  const res = await fetch(`${API_URL}/api/public/pengumuman${query}`);
  if (!res.ok) throw new Error('Gagal memuat pengumuman unit');
  return res.json();
};

export const fetchPengumumanUnitBySlug = async (slug) => {
  const res = await fetch(`${API_URL}/api/public/pengumuman/${encodeURIComponent(slug)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Gagal memuat pengumuman');
  return res.json();
};

/// Bagan struktur organisasi yang dikelola lewat panel admin sendiri —
/// berbeda dari fetchStrukturOrganisasi() di atas yang masih membaca koleksi
/// lama di CMS dan dipakai halaman detail Program Studi.
export const fetchBaganOrganisasi = async () => {
  const res = await fetch(`${API_URL}/api/public/struktur-organisasi`);
  if (!res.ok) throw new Error('Gagal memuat struktur organisasi');
  return res.json();
};

export const fetchUnitAnggota = async (unit) => {
  const res = await fetch(`${API_URL}/api/public/unit/${encodeURIComponent(unit)}/anggota`);
  if (!res.ok) throw new Error('Gagal memuat anggota unit');
  return res.json();
};

export const fetchTendikBySlug = fetchTendikByIdentifier;

export const fetchBeasiswa = async () => {
  const res = await fetch(`${API_URL}/api/public/beasiswa`);
  if (!res.ok) throw new Error('Gagal memuat data beasiswa');
  return res.json();
};

export const fetchPrestasiMahasiswa = async () => {
  const res = await fetch(`${API_URL}/api/public/prestasi-mahasiswa`);
  if (!res.ok) throw new Error('Gagal memuat prestasi mahasiswa');
  return res.json();
};

export const fetchPrestasiMahasiswaById = async (id) => {
  const res = await fetch(`${API_URL}/api/public/prestasi-mahasiswa/${encodeURIComponent(id)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Gagal memuat detail prestasi');
  return res.json();
};

export const fetchPrestasiMahasiswaBySlug = async (slug) => {
  const res = await fetch(`${API_URL}/api/public/prestasi-mahasiswa/${encodeURIComponent(slug)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Gagal memuat detail prestasi');
  return res.json();
};

export const fetchUKM = async () => {
  const res = await fetch(`${API_URL}/api/public/ukm`);
  if (!res.ok) throw new Error('Gagal memuat data UKM');
  return res.json();
};

export const fetchUKMBySlug = async (slug) => {
  const res = await fetch(`${API_URL}/api/public/ukm/${encodeURIComponent(slug)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Gagal memuat detail UKM');
  return res.json();
};

export const fetchBeritaList = async (limit = 100) => {
  try {
    const res = await fetch(`${API_URL}/api/public/berita?limit=${limit}`);
    if (!res.ok) throw new Error('Gagal memuat berita');
    return await res.json();
  } catch (err) {
    console.error('Error fetching berita list:', err);
    return [];
  }
};

/**
 * Galeri foto berita kini menyatu dalam respons `/api/public/berita/:slug`
 * (field `galeri: [{ id, url, caption }]`). Dipertahankan sebagai stub supaya
 * pemanggil lama tidak error selama transisi.
 */
export const fetchBeritaGallery = async () => [];

export const fetchBeritaBySlug = async (slug) => {
  try {
    const res = await fetch(`${API_URL}/api/public/berita/${encodeURIComponent(slug)}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error('Gagal memuat berita');
    return await res.json();
  } catch (err) {
    console.error('Error fetching berita by slug:', err);
    return null;
  }
};

export const fetchBeritaRelated = async (kategori, excludeId, limit = 3) => {
  try {
    const params = new URLSearchParams({ limit: String(limit) });
    if (kategori) params.set('category', kategori);
    if (excludeId) params.set('exclude', String(excludeId));
    const res = await fetch(`${API_URL}/api/public/berita?${params.toString()}`);
    if (!res.ok) throw new Error('Gagal memuat berita terkait');
    return await res.json();
  } catch (err) {
    console.error('Error fetching related berita:', err);
    return [];
  }
};

const BULAN_ID = ['Januari','Februari','Maret','April','Mei','Juni',
  'Juli','Agustus','September','Oktober','November','Desember'];

export const formatTanggalIndonesia = (isoString) => {
  if (!isoString) return '-';
  try {
    const [year, month, day] = isoString.split('T')[0].split('-');
    return `${parseInt(day)} ${BULAN_ID[parseInt(month) - 1]} ${year}`;
  } catch {
    return '-';
  }
};

/// Semua sumber gambar kini mengirim URL penuh domain media sendiri (atau
/// null). URL dilewatkan apa adanya, cukup ditambah lebar yang diminta —
/// transformasi lain (tinggi/kualitas/format) ditangani di sisi server media.
export const getImageUrl = (fileId, options = {}) => {
  if (!fileId) return null;
  if (/^https?:\/\//i.test(fileId)) {
    const width = options.width || 800;
    return `${fileId}${fileId.includes('?') ? '&' : '?'}w=${width}`;
  }
  return null;
};

export async function submitTracerStudy(data) {
  const res = await fetch(`${API_URL}/api/public/tracer-study`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.error || 'Gagal mengirim data tracer study.');
  }
  return json;
}

export async function fetchTracerStats() {
  const res = await fetch(`${API_URL}/api/public/tracer-study/stats`);
  if (!res.ok) throw new Error('Gagal memuat statistik tracer study.');
  return res.json();
}

export async function fetchTracerStories() {
  const res = await fetch(`${API_URL}/api/public/tracer-study/stories`);
  if (!res.ok) throw new Error('Gagal memuat testimoni alumni.');
  return res.json();
}

